// ContactSection organism component
import { useState } from "react";
import { Wrench, Car, HeartHandshake, HelpCircle } from "lucide-react";
import { useLandingContent } from "../../contexts/LandingContentContext";
import { Heading } from "../atoms";
import { FormField } from "../molecules";
import { Button } from "../atoms";
import { jobService } from "../../services/jobService";

const CATEGORY_JOB_TYPE = {
  "home-repair": "HomeRepair",
  "car-repair": "CarRepair",
  "general-help": "Requested",
};

const CATEGORY_ICONS = {
  "home-repair": Wrench,
  "car-repair": Car,
  "general-help": HeartHandshake,
};

export const ContactSection = () => {
  const { contact } = useLandingContent();
  const categories = contact.helpCategories || [];

  const [selectedCategory, setSelectedCategory] = useState(0);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    town: "",
    message: "",
  });
  const [images, setImages] = useState([]);
  const [formStatus, setFormStatus] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length + images.length > 5) {
      setErrors({ ...errors, images: "Maximum 5 images allowed" });
      return;
    }
    setImages([...images, ...files]);
    setErrors({ ...errors, images: "" });
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    if (formData.email.trim() && !validateEmail(formData.email))
      newErrors.email = "Invalid email address";
    if (!formData.town.trim()) newErrors.town = "Town or postcode is required";
    if (!formData.message.trim())
      newErrors.message = "Please tell us what you need help with";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setLoading(true);
    setFormStatus("");

    const selectedCat = categories[selectedCategory];
    const jobType = selectedCat ? CATEGORY_JOB_TYPE[selectedCat.id] : undefined;
    const fullDescription = formData.town.trim()
      ? `[${formData.town.trim()}] ${formData.message.trim()}`
      : formData.message.trim();

    try {
      await jobService.contactMe(
        formData.name.trim(),
        formData.email.trim() || "not-provided@compassionforwidows.org",
        formData.phone.trim(),
        fullDescription,
        images,
        jobType,
      );
      setFormStatus("Thank you! Your message has been sent. God bless you.");
      setFormData({ name: "", email: "", phone: "", town: "", message: "" });
      setImages([]);
      setTimeout(() => setFormStatus(""), 5000);
    } catch (error) {
      setFormStatus("Failed to send message. Please try again.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-paper">
      <div className="max-w-5xl mx-auto px-6">
        {/* Section header */}
        <div className="text-center mb-12">
          <p className="font-display text-terracotta-600 text-sm uppercase tracking-widest mb-3">
            We'd Love to Hear From You
          </p>
          <Heading level={2} className="font-display text-ink">
            {contact.heading}
          </Heading>
          {contact.body && (
            <p className="mt-4 text-ink-soft text-base max-w-2xl mx-auto leading-relaxed">
              {contact.body}
            </p>
          )}
        </div>

        {/* Category tabs */}
        {categories.length > 0 && (
          <div className="grid grid-cols-3 gap-3 mb-10">
            {categories.map((cat, i) => (
              <button
                key={cat.id || i}
                type="button"
                onClick={() => setSelectedCategory(i)}
                className={`flex flex-col items-center gap-2 p-3 sm:flex-row sm:items-center sm:gap-3 sm:p-4 sm:text-left rounded-2xl border-2 transition-all text-center w-full ${
                  selectedCategory === i
                    ? "bg-ink-teal border-ink-teal text-white"
                    : "bg-white border-terracotta-100 text-ink hover:border-terracotta-300"
                }`}
              >
                <span
                  className={`w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0 ${
                    selectedCategory === i ? "bg-white/20" : "bg-terracotta-100"
                  }`}
                >
                  {(() => {
                    const IconComponent = CATEGORY_ICONS[cat.id] || HelpCircle;
                    return (
                      <IconComponent
                        className={`w-5 h-5 ${selectedCategory === i ? "text-white" : "text-terracotta-600"}`}
                        strokeWidth={1.75}
                      />
                    );
                  })()}
                </span>
                <div className="min-w-0 sm:min-w-0">
                  <div className="font-display font-semibold text-xs sm:text-sm leading-snug sm:truncate">
                    {cat.label}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Two-column layout */}
        <div className="grid md:grid-cols-[260px,1fr] gap-8 items-start">
          {/* Left: explainer panel */}
          {categories.length > 0 && (
            <div className="bg-white rounded-2xl border border-terracotta-100 p-6">
              <h3 className="font-display text-ink text-base font-semibold mb-4">
                We can help with
              </h3>
              <ul className="space-y-3">
                {(categories[selectedCategory]?.items || []).map((item, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2.5 text-sm text-ink-soft"
                  >
                    <svg
                      className="w-4 h-4 text-ink-teal mt-0.5 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-xs text-ink-soft border-t border-terracotta-100 pt-4 leading-relaxed">
                Not sure if your need fits? Ask anyway. If we can't help, we'll
                know someone who can.
              </p>
            </div>
          )}

          {/* Right: form */}
          <div>
            <form
              onSubmit={handleContactSubmit}
              className="bg-cream rounded-2xl shadow-soft border border-terracotta-50 p-8 space-y-5"
            >
              <FormField
                label="Your name"
                placeholder="e.g. Margaret Wilson"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                error={errors.name}
                required
              />
              <div className="grid sm:grid-cols-2 gap-5">
                <FormField
                  label="Phone number"
                  type="tel"
                  placeholder="So we can call you back"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  error={errors.phone}
                  required
                />
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-ink">
                      Email
                    </label>
                    <span className="text-xs text-ink-soft">Optional</span>
                  </div>
                  <input
                    type="email"
                    placeholder="If you prefer email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="w-full px-4 py-3 bg-paper border border-terracotta-200 rounded-xl text-ink placeholder-ink-soft focus:outline-none focus:ring-2 focus:ring-terracotta-400 focus:border-transparent transition-shadow"
                  />
                  {errors.email && (
                    <p className="text-red-600 text-sm mt-1">{errors.email}</p>
                  )}
                </div>
              </div>
              <FormField
                label="Town or postcode"
                placeholder="So we can match you with a local volunteer"
                value={formData.town}
                onChange={(e) =>
                  setFormData({ ...formData, town: e.target.value })
                }
                error={errors.town}
                required
              />

              {/* Message textarea */}
              <div>
                <label className="block text-sm font-semibold text-ink mb-2">
                  What do you need help with?{" "}
                  <span className="text-red-500">*</span>
                </label>
                <textarea
                  placeholder="In your own words. Don't worry about technical details, just tell us what's wrong or what you need."
                  rows="5"
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  className="w-full px-4 py-3 bg-paper border border-terracotta-200 rounded-xl text-ink placeholder-ink-soft focus:outline-none focus:ring-2 focus:ring-terracotta-400 focus:border-transparent transition-shadow resize-none"
                />
                {errors.message && (
                  <p className="text-red-600 text-sm mt-1">{errors.message}</p>
                )}
              </div>

              {/* File upload dropzone */}
              <div>
                <label className="block text-sm font-semibold text-ink mb-2">
                  Attach Images{" "}
                  <span className="text-ink-soft font-normal">
                    (optional, max 5)
                  </span>
                </label>
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                    className="hidden"
                    id="contact-file-upload"
                    disabled={images.length >= 5}
                  />
                  <label
                    htmlFor="contact-file-upload"
                    className={`flex flex-col items-center justify-center w-full px-6 py-8 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                      images.length >= 5
                        ? "border-terracotta-100 bg-terracotta-50/40 cursor-not-allowed opacity-60"
                        : "border-terracotta-300 bg-paper hover:bg-terracotta-50 hover:border-terracotta-500"
                    }`}
                  >
                    <svg
                      className={`h-10 w-10 mb-3 ${images.length >= 5 ? "text-ink-soft" : "text-terracotta-400"}`}
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    {images.length >= 5 ? (
                      <p className="text-sm font-semibold text-ink-soft">
                        Maximum images reached
                      </p>
                    ) : (
                      <p className="text-sm text-center">
                        <span className="font-semibold text-terracotta-600">
                          Click to upload
                        </span>
                        <span className="text-ink-soft"> or drag and drop</span>
                      </p>
                    )}
                    <p className="mt-1 text-xs text-ink-soft">
                      PNG, JPG, GIF up to 10MB &nbsp;·&nbsp; {images.length}/5
                      images
                    </p>
                  </label>
                </div>

                {errors.images && (
                  <p className="text-red-600 text-sm mt-1">{errors.images}</p>
                )}

                {images.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {images.map((image, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-paper px-4 py-3 rounded-xl border border-terracotta-100 shadow-soft hover:shadow-lift transition-shadow"
                      >
                        <div className="flex items-center flex-1 min-w-0">
                          <svg
                            className="h-5 w-5 text-terracotta-500 mr-2.5 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                          <span className="text-sm text-ink truncate min-w-0 flex-1 block">
                            {image.name.slice(0, 12) + "..."}
                          </span>
                          <span className="ml-2 text-xs text-ink-soft flex-shrink-0 whitespace-nowrap">
                            ({(image.size / 1024).toFixed(1)} KB)
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="ml-3 p-1 text-terracotta-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors flex-shrink-0"
                          aria-label="Remove image"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Button
                variant="primary"
                className="w-full py-3.5 font-display tracking-wide"
                type="submit"
                disabled={loading}
              >
                {loading ? "Sending..." : "Send Message"}
              </Button>

              {formStatus && (
                <p
                  className={`mt-2 text-center text-sm font-medium px-4 py-3 rounded-xl ${
                    formStatus.includes("Failed")
                      ? "text-red-700 bg-red-50"
                      : "text-ink-teal bg-terracotta-50"
                  }`}
                >
                  {formStatus}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
